/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Biblio API Adapter
 * API contract for the Biblio API adapter service.
 *
 * ## Adapter principles
 *
 * This adapter exposes two controlled subsets of the `Biblio API`: a browser-safe subset for end-user clients and a restricted subset for authorized server-to-server clients. Some endpoints are available in both subsets.
 *
 * - **Bearer-only client auth**: clients send one opaque `bearerAuth` token to the adapter.
 *   `Biblio API` credentials (`x-api-key` and `Biblio API` bearer token) are handled internally in the adapter.
 * - **User context derivation**: client-supplied `user_id` is not accepted on user-scoped
 *   operations. See **Authentication and user identity** below.
 * - **Automatic user provisioning**: the adapter ensures that authenticated users exist
 *   in `Biblio API` by internally calling `POST /v1/auth/create-sign-in-token` with
 *   `create_if_not_exists: true`. The created user is associated with the resolved
 *   `user_id` and `organization_third_party_id` (see **Authentication and user identity**).
 * - **Response transparency**: responses include `x-response-source` (`adapter` or `wedobooks`)
 *   and `x-request-id` for correlation and debugging.
 *
 * ## Authentication and user identity
 *
 * **Token**
 *
 * Obtain an OAuth2 access token from [login.bib.dk](https://login.bib.dk) and send it as `Authorization: Bearer <token>`.
 *
 * **Endpoint access**
 *
 * Endpoints that read or change a user's loans, reservations, or user data require a token representing the end user, issued via the OAuth2 `authorization_code` grant.
 *
 * Metadata and sample endpoints can be accessed with either an end-user token or a server-to-server token.
 *
 * `GET /v1/organizations` accepts any valid bearer token. If the token resolves to an end user, the client may omit `third_party_id`; the adapter then uses the user's municipality. Otherwise, the client must send `organization_id` or `third_party_id`.
 *
 * Server-to-server access requires a token issued via the OAuth2 `client_credentials` grant. The OAuth client must be configured with access to the endpoint.
 *
 * **Identity resolution**
 *
 * User-scoped endpoints do not accept client-supplied `user_id` or organization ids. Resolve them from login.bib.dk userinfo `attributes` as follows:
 *
 * 1. If both `uniqueId` and `municipality` are present:
 *    - `user_id` = `uniqueId`
 *    - `organization_third_party_id` = `municipality`
 *    - `user_data.type` = `"library"`
 * 2. Otherwise, use the Unilogin fallback:
 *    - `user_id` = `uniloginUniIdHash`
 *    - `organization_third_party_id` = `uniloginMunicipality`
 *    - `user_data.type` = `"unilogin"`
 *    - `user_data.institution_id` = the first id in `uniloginInstitutionIds`
 *
 *
 * Unilogin fallback requires at least one institution id and an active Unilogin license. Without an active license, the adapter returns 403 with `User is missing license`.
 *
 * If identity cannot be resolved, the adapter returns 403 Forbidden.
 * ## Unsupported endpoints in adapter
 *
 * These endpoints are available in the `Biblio API`, but not in this adapter.
 *
 * - `POST /v1/auth/sign-in` (used internally)
 * - `POST /v1/auth/create-user`
 * - `Webhooks` (Biblio API outbound webhook events)
 * - `GET /v1/inventory`
 * - `GET /v1/shelves`
 * - `GET /v1/onix/{material_id}`
 * - `GET /v1/onix/pull/alpha`
 * - `GET /v1/onix/pull/delta/{from_utc_timestamp}`
 *
 * OpenAPI spec version: 1.0.0
 */
import type {
  AcceptReservationOfferApiResponse,
  AcceptReservationOfferForAuthenticatedUserBody,
  ApiErrorResponse,
  CanLoanApiResponse,
  CanLoanForAuthenticatedUserParams,
  CreateLoanApiResponse,
  CreateReservationApiResponse,
  CreateReservationForAuthenticatedUserBody,
  CreateSignInTokenApiResponse,
  DeleteReservationApiResponse,
  GetLoanApiResponse,
  GetLoanQuotasApiResponse,
  GetLoansApiResponse,
  GetLoansForAuthenticatedUserParams,
  GetMetadataApiResponse,
  GetMetadataByMaterialIdsParams,
  GetOrganizationApiResponse,
  GetOrganizationConfigsApiResponse,
  GetOrganizationConfigsParams,
  GetOrganizationParams,
  GetReservationApiResponse,
  GetReservationOffersApiResponse,
  GetReservationOffersForAuthenticatedUserParams,
  GetReservationsApiResponse,
  GetReservationsForAuthenticatedUserParams,
  GetSampleApiResponse,
  GetSampleParams,
  GetSupportIdApiResponse,
  RequestLoanForAuthenticatedUserBody,
} from "./model"

export type createSignInTokenForAuthenticatedUserResponse200 = {
  data: CreateSignInTokenApiResponse
  status: 200
}

export type createSignInTokenForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type createSignInTokenForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type createSignInTokenForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type createSignInTokenForAuthenticatedUserResponseSuccess =
  createSignInTokenForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type createSignInTokenForAuthenticatedUserResponseError = (
  | createSignInTokenForAuthenticatedUserResponse401
  | createSignInTokenForAuthenticatedUserResponse403
  | createSignInTokenForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type createSignInTokenForAuthenticatedUserResponse =
  | createSignInTokenForAuthenticatedUserResponseSuccess
  | createSignInTokenForAuthenticatedUserResponseError

export const getCreateSignInTokenForAuthenticatedUserUrl = () => {
  return `/v1/auth/create-sign-in-token`
}

/**
 * Creates a custom sign-in token for the authenticated user.
 * The returned `token` is not itself the bearer token for the `Biblio API`. A client must exchange it with authentication and then use the resulting ID token as bearer auth.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter derives `user_id`, `create_if_not_exists`, and `user_data` from >   login.bib.dk userinfo (see **Authentication and user identity**), so callers must >   not send a request body.
 * @summary Create
 */
export const createSignInTokenForAuthenticatedUser = async (
  options?: RequestInit
): Promise<createSignInTokenForAuthenticatedUserResponse> => {
  const res = await fetch(getCreateSignInTokenForAuthenticatedUserUrl(), {
    ...options,
    method: "POST",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: createSignInTokenForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createSignInTokenForAuthenticatedUserResponse
}

export type getLoansForAuthenticatedUserResponse200 = {
  data: GetLoansApiResponse
  status: 200
}

export type getLoansForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getLoansForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getLoansForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getLoansForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getLoansForAuthenticatedUserResponseSuccess =
  getLoansForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getLoansForAuthenticatedUserResponseError = (
  | getLoansForAuthenticatedUserResponse401
  | getLoansForAuthenticatedUserResponse403
  | getLoansForAuthenticatedUserResponse422
  | getLoansForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getLoansForAuthenticatedUserResponse =
  getLoansForAuthenticatedUserResponseSuccess | getLoansForAuthenticatedUserResponseError

export const getGetLoansForAuthenticatedUserUrl = (params?: GetLoansForAuthenticatedUserParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0 ? `/v1/loans?${stringifiedParams}` : `/v1/loans`
}

/**
 * Returns a paginated list of loans.
 * By default only active loans are returned because `active` defaults to `true`. Set
 * `active=false` to retrieve ended or inactive loans instead.
 * Pagination uses the returned loan id as the next `cursor`.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Get loans
 */
export const getLoansForAuthenticatedUser = async (
  params?: GetLoansForAuthenticatedUserParams,
  options?: RequestInit
): Promise<getLoansForAuthenticatedUserResponse> => {
  const res = await fetch(getGetLoansForAuthenticatedUserUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getLoansForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getLoansForAuthenticatedUserResponse
}

export type requestLoanForAuthenticatedUserResponse201 = {
  data: CreateLoanApiResponse | ApiErrorResponse
  status: 201
}

export type requestLoanForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type requestLoanForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type requestLoanForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type requestLoanForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type requestLoanForAuthenticatedUserResponseSuccess =
  requestLoanForAuthenticatedUserResponse201 & {
    headers: Headers
  }
export type requestLoanForAuthenticatedUserResponseError = (
  | requestLoanForAuthenticatedUserResponse401
  | requestLoanForAuthenticatedUserResponse403
  | requestLoanForAuthenticatedUserResponse422
  | requestLoanForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type requestLoanForAuthenticatedUserResponse =
  requestLoanForAuthenticatedUserResponseSuccess | requestLoanForAuthenticatedUserResponseError

export const getRequestLoanForAuthenticatedUserUrl = () => {
  return `/v1/loans`
}

/**
 * Attempts to create a loan for a user and material immediately.
 * This endpoint performs the same availability and quota checks as `GET /v1/loans/can-loan`.
 * When a loan is created, the response status is `201` and the `loan` object is populated.
 * When the material cannot be loaned, the response is still a successful API response with a decision
 * status and optional rejection details, but without a `loan`.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Request loan for user
 */
export const requestLoanForAuthenticatedUser = async (
  requestLoanForAuthenticatedUserBody: RequestLoanForAuthenticatedUserBody,
  options?: RequestInit
): Promise<requestLoanForAuthenticatedUserResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {}
    if (h instanceof Headers) return Object.fromEntries(h.entries())
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, entry => Array.from(entry) as [string, string])
      )
    }
    const headers: Record<string, string | readonly string[]> = {}
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value
    }
    return headers
  }
  const res = await fetch(getRequestLoanForAuthenticatedUserUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(requestLoanForAuthenticatedUserBody),
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: requestLoanForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as requestLoanForAuthenticatedUserResponse
}

export type getLoanForAuthenticatedUserResponse200 = {
  data: GetLoanApiResponse | ApiErrorResponse
  status: 200
}

export type getLoanForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getLoanForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getLoanForAuthenticatedUserResponse404 = {
  data: void
  status: 404
}

export type getLoanForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getLoanForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getLoanForAuthenticatedUserResponseSuccess = getLoanForAuthenticatedUserResponse200 & {
  headers: Headers
}
export type getLoanForAuthenticatedUserResponseError = (
  | getLoanForAuthenticatedUserResponse401
  | getLoanForAuthenticatedUserResponse403
  | getLoanForAuthenticatedUserResponse404
  | getLoanForAuthenticatedUserResponse422
  | getLoanForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getLoanForAuthenticatedUserResponse =
  getLoanForAuthenticatedUserResponseSuccess | getLoanForAuthenticatedUserResponseError

export const getGetLoanForAuthenticatedUserUrl = (loanId: string) => {
  return `/v1/loans/${loanId}`
}

/**
 * Returns a single loan by `loan_id`.
 * Use this endpoint to refresh the authoritative state of a known loan after creation,
 * or when synchronizing client state with Biblio API loan status.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter verifies that returned `loan.uid` matches the derived `user_id`.
 * > - If the loan belongs to another user, the adapter returns `404` so the resource appears non-existent.
 * @summary Get loan
 */
export const getLoanForAuthenticatedUser = async (
  loanId: string,
  options?: RequestInit
): Promise<getLoanForAuthenticatedUserResponse> => {
  const res = await fetch(getGetLoanForAuthenticatedUserUrl(loanId), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getLoanForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getLoanForAuthenticatedUserResponse
}

export type canLoanForAuthenticatedUserResponse200 = {
  data: CanLoanApiResponse | ApiErrorResponse
  status: 200
}

export type canLoanForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type canLoanForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type canLoanForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type canLoanForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type canLoanForAuthenticatedUserResponseSuccess = canLoanForAuthenticatedUserResponse200 & {
  headers: Headers
}
export type canLoanForAuthenticatedUserResponseError = (
  | canLoanForAuthenticatedUserResponse401
  | canLoanForAuthenticatedUserResponse403
  | canLoanForAuthenticatedUserResponse422
  | canLoanForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type canLoanForAuthenticatedUserResponse =
  canLoanForAuthenticatedUserResponseSuccess | canLoanForAuthenticatedUserResponseError

export const getCanLoanForAuthenticatedUserUrl = (params: CanLoanForAuthenticatedUserParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0
    ? `/v1/loans/can-loan?${stringifiedParams}`
    : `/v1/loans/can-loan`
}

/**
 * Checks whether a user can currently loan a specific material without creating a loan.
 * Use this as a preflight check when you want to show availability, provider choice, or quota
 * rejection details before calling `POST /v1/loans`.
 * The result reflects the state at request time only and should not be treated as a reservation
 * or guarantee that a later loan request will still succeed.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Material loan availability for user
 */
export const canLoanForAuthenticatedUser = async (
  params: CanLoanForAuthenticatedUserParams,
  options?: RequestInit
): Promise<canLoanForAuthenticatedUserResponse> => {
  const res = await fetch(getCanLoanForAuthenticatedUserUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: canLoanForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as canLoanForAuthenticatedUserResponse
}

export type getLoanQuotasForAuthenticatedUserResponse200 = {
  data: GetLoanQuotasApiResponse | ApiErrorResponse
  status: 200
}

export type getLoanQuotasForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getLoanQuotasForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getLoanQuotasForAuthenticatedUserResponse404 = {
  data: void
  status: 404
}

export type getLoanQuotasForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getLoanQuotasForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getLoanQuotasForAuthenticatedUserResponseSuccess =
  getLoanQuotasForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getLoanQuotasForAuthenticatedUserResponseError = (
  | getLoanQuotasForAuthenticatedUserResponse401
  | getLoanQuotasForAuthenticatedUserResponse403
  | getLoanQuotasForAuthenticatedUserResponse404
  | getLoanQuotasForAuthenticatedUserResponse422
  | getLoanQuotasForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getLoanQuotasForAuthenticatedUserResponse =
  getLoanQuotasForAuthenticatedUserResponseSuccess | getLoanQuotasForAuthenticatedUserResponseError

export const getGetLoanQuotasForAuthenticatedUserUrl = () => {
  return `/v1/users/get_loan_quotas`
}

/**
 * Returns borrowing quota information for a user across every organization the user belongs to.
 * Use this endpoint when you need to show remaining loan capacity or organization-specific quota
 * rules before attempting a loan.
 *
 * The response may contain multiple entries for the same user, one per organization. Each entry
 * contains the organization id and name, ancestor ids, whether quotas are split by format or
 * shared across formats, and only the relevant quota fields for that mode.
 *
 * Treat this as informational state at request time. Loan creation endpoints remain authoritative
 * because availability and quota usage can change between calls.
 *
 * When `split_on_format` is `true`, the response includes per-format loan limits and usage for
 * audiobooks and ebooks. When `split_on_format` is `false`, the response includes combined loan
 * limits and usage across formats.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Get user loan quotas
 */
export const getLoanQuotasForAuthenticatedUser = async (
  options?: RequestInit
): Promise<getLoanQuotasForAuthenticatedUserResponse> => {
  const res = await fetch(getGetLoanQuotasForAuthenticatedUserUrl(), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getLoanQuotasForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getLoanQuotasForAuthenticatedUserResponse
}

export type getSupportIdForAuthenticatedUserResponse200 = {
  data: GetSupportIdApiResponse | ApiErrorResponse
  status: 200
}

export type getSupportIdForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getSupportIdForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getSupportIdForAuthenticatedUserResponse404 = {
  data: void
  status: 404
}

export type getSupportIdForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getSupportIdForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getSupportIdForAuthenticatedUserResponseSuccess =
  getSupportIdForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getSupportIdForAuthenticatedUserResponseError = (
  | getSupportIdForAuthenticatedUserResponse401
  | getSupportIdForAuthenticatedUserResponse403
  | getSupportIdForAuthenticatedUserResponse404
  | getSupportIdForAuthenticatedUserResponse422
  | getSupportIdForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getSupportIdForAuthenticatedUserResponse =
  getSupportIdForAuthenticatedUserResponseSuccess | getSupportIdForAuthenticatedUserResponseError

export const getGetSupportIdForAuthenticatedUserUrl = () => {
  return `/v1/users/get_support_id`
}

/**
 * Returns the support id for a user.
 * If the user does not already have a support id, one is generated and persisted before the
 * response is returned.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Get user support id
 */
export const getSupportIdForAuthenticatedUser = async (
  options?: RequestInit
): Promise<getSupportIdForAuthenticatedUserResponse> => {
  const res = await fetch(getGetSupportIdForAuthenticatedUserUrl(), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getSupportIdForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getSupportIdForAuthenticatedUserResponse
}

export type getReservationsForAuthenticatedUserResponse200 = {
  data: GetReservationsApiResponse
  status: 200
}

export type getReservationsForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getReservationsForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getReservationsForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getReservationsForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getReservationsForAuthenticatedUserResponseSuccess =
  getReservationsForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getReservationsForAuthenticatedUserResponseError = (
  | getReservationsForAuthenticatedUserResponse401
  | getReservationsForAuthenticatedUserResponse403
  | getReservationsForAuthenticatedUserResponse422
  | getReservationsForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getReservationsForAuthenticatedUserResponse =
  | getReservationsForAuthenticatedUserResponseSuccess
  | getReservationsForAuthenticatedUserResponseError

export const getGetReservationsForAuthenticatedUserUrl = (
  params?: GetReservationsForAuthenticatedUserParams
) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0 ? `/v1/reservations?${stringifiedParams}` : `/v1/reservations`
}

/**
 * Returns a paginated list of reservations.
 * When `limit` is omitted, the default page size is `25`.
 * The returned pagination cursor is the reservation id to send back on the next request.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * > - The adapter does not support listing reservations across users.
 * @summary Get reservations.
 */
export const getReservationsForAuthenticatedUser = async (
  params?: GetReservationsForAuthenticatedUserParams,
  options?: RequestInit
): Promise<getReservationsForAuthenticatedUserResponse> => {
  const res = await fetch(getGetReservationsForAuthenticatedUserUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getReservationsForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getReservationsForAuthenticatedUserResponse
}

export type createReservationForAuthenticatedUserResponse200 = {
  data: CreateReservationApiResponse
  status: 200
}

export type createReservationForAuthenticatedUserResponse201 = {
  data: CreateReservationApiResponse
  status: 201
}

export type createReservationForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type createReservationForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type createReservationForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type createReservationForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type createReservationForAuthenticatedUserResponseSuccess = (
  | createReservationForAuthenticatedUserResponse200
  | createReservationForAuthenticatedUserResponse201
) & {
  headers: Headers
}
export type createReservationForAuthenticatedUserResponseError = (
  | createReservationForAuthenticatedUserResponse401
  | createReservationForAuthenticatedUserResponse403
  | createReservationForAuthenticatedUserResponse422
  | createReservationForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type createReservationForAuthenticatedUserResponse =
  | createReservationForAuthenticatedUserResponseSuccess
  | createReservationForAuthenticatedUserResponseError

export const getCreateReservationForAuthenticatedUserUrl = () => {
  return `/v1/reservations`
}

/**
 * Requests a reservation for a user and material.
 *
 * If the material can be loaned right away, the endpoint creates a loan instead of a waiting
 * reservation. Status `201` means a new reservation or a loan was created and is in the
 * response. Status `200` means nothing new was written; a reservation the user already held is
 * still included.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Create reservation
 */
export const createReservationForAuthenticatedUser = async (
  createReservationForAuthenticatedUserBody: CreateReservationForAuthenticatedUserBody,
  options?: RequestInit
): Promise<createReservationForAuthenticatedUserResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {}
    if (h instanceof Headers) return Object.fromEntries(h.entries())
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, entry => Array.from(entry) as [string, string])
      )
    }
    const headers: Record<string, string | readonly string[]> = {}
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value
    }
    return headers
  }
  const res = await fetch(getCreateReservationForAuthenticatedUserUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(createReservationForAuthenticatedUserBody),
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: createReservationForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as createReservationForAuthenticatedUserResponse
}

export type acceptReservationOfferForAuthenticatedUserResponse200 = {
  data: AcceptReservationOfferApiResponse
  status: 200
}

export type acceptReservationOfferForAuthenticatedUserResponse201 = {
  data: AcceptReservationOfferApiResponse
  status: 201
}

export type acceptReservationOfferForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type acceptReservationOfferForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type acceptReservationOfferForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type acceptReservationOfferForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type acceptReservationOfferForAuthenticatedUserResponseSuccess = (
  | acceptReservationOfferForAuthenticatedUserResponse200
  | acceptReservationOfferForAuthenticatedUserResponse201
) & {
  headers: Headers
}
export type acceptReservationOfferForAuthenticatedUserResponseError = (
  | acceptReservationOfferForAuthenticatedUserResponse401
  | acceptReservationOfferForAuthenticatedUserResponse403
  | acceptReservationOfferForAuthenticatedUserResponse422
  | acceptReservationOfferForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type acceptReservationOfferForAuthenticatedUserResponse =
  | acceptReservationOfferForAuthenticatedUserResponseSuccess
  | acceptReservationOfferForAuthenticatedUserResponseError

export const getAcceptReservationOfferForAuthenticatedUserUrl = () => {
  return `/v1/reservations/accept-offer`
}

/**
 * Accepts an active reservation offer and converts it into a loan.
 * Either `offer_id` or `reservation_id` can be used.
 *
 * A reservation offer temporarily holds the relevant license for the title and price model.
 * On expiry/cancellation the license will be released.
 * On success, the response includes the created `loan_id` and uses status `201`.
 *
 * The reservation and the offer are cleaned up shortly after acceptance, so external systems
 * should not expect those records to remain available indefinitely.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * @summary Accept reservation offer
 */
export const acceptReservationOfferForAuthenticatedUser = async (
  acceptReservationOfferForAuthenticatedUserBody: AcceptReservationOfferForAuthenticatedUserBody,
  options?: RequestInit
): Promise<acceptReservationOfferForAuthenticatedUserResponse> => {
  const getHeaders = (
    h?: NonNullable<RequestInit["headers"]>
  ): Record<string, string | readonly string[]> => {
    if (!h) return {}
    if (h instanceof Headers) return Object.fromEntries(h.entries())
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, entry => Array.from(entry) as [string, string])
      )
    }
    const headers: Record<string, string | readonly string[]> = {}
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value
    }
    return headers
  }
  const res = await fetch(getAcceptReservationOfferForAuthenticatedUserUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(acceptReservationOfferForAuthenticatedUserBody),
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: acceptReservationOfferForAuthenticatedUserResponse["data"] = body
    ? JSON.parse(body)
    : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as acceptReservationOfferForAuthenticatedUserResponse
}

export type getReservationOffersForAuthenticatedUserResponse200 = {
  data: GetReservationOffersApiResponse
  status: 200
}

export type getReservationOffersForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getReservationOffersForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getReservationOffersForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getReservationOffersForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getReservationOffersForAuthenticatedUserResponseSuccess =
  getReservationOffersForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getReservationOffersForAuthenticatedUserResponseError = (
  | getReservationOffersForAuthenticatedUserResponse401
  | getReservationOffersForAuthenticatedUserResponse403
  | getReservationOffersForAuthenticatedUserResponse422
  | getReservationOffersForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getReservationOffersForAuthenticatedUserResponse =
  | getReservationOffersForAuthenticatedUserResponseSuccess
  | getReservationOffersForAuthenticatedUserResponseError

export const getGetReservationOffersForAuthenticatedUserUrl = (
  params?: GetReservationOffersForAuthenticatedUserParams
) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0
    ? `/v1/reservations/offers?${stringifiedParams}`
    : `/v1/reservations/offers`
}

/**
 * Returns a paginated list of reservation offers for the authenticated user.
 * Use this endpoint to discover offers before calling `POST /v1/reservations/accept-offer`,
 * or to inspect offer status and timestamps.
 * The returned pagination cursor is the reservation offer id to send back on the next request.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter forwards the derived `user_id` (see **Authentication and user identity**).
 * @summary Get reservation offers
 */
export const getReservationOffersForAuthenticatedUser = async (
  params?: GetReservationOffersForAuthenticatedUserParams,
  options?: RequestInit
): Promise<getReservationOffersForAuthenticatedUserResponse> => {
  const res = await fetch(getGetReservationOffersForAuthenticatedUserUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getReservationOffersForAuthenticatedUserResponse["data"] = body
    ? JSON.parse(body)
    : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getReservationOffersForAuthenticatedUserResponse
}

export type getReservationForAuthenticatedUserResponse200 = {
  data: GetReservationApiResponse
  status: 200
}

export type getReservationForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type getReservationForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type getReservationForAuthenticatedUserResponse404 = {
  data: void
  status: 404
}

export type getReservationForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type getReservationForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type getReservationForAuthenticatedUserResponseSuccess =
  getReservationForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type getReservationForAuthenticatedUserResponseError = (
  | getReservationForAuthenticatedUserResponse401
  | getReservationForAuthenticatedUserResponse403
  | getReservationForAuthenticatedUserResponse404
  | getReservationForAuthenticatedUserResponse422
  | getReservationForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type getReservationForAuthenticatedUserResponse =
  | getReservationForAuthenticatedUserResponseSuccess
  | getReservationForAuthenticatedUserResponseError

export const getGetReservationForAuthenticatedUserUrl = (reservationId: string) => {
  return `/v1/reservations/${reservationId}`
}

/**
 * Returns a single reservation by `reservation_id`.
 * Use this to refresh the authoritative state of a known reservation.
 * A previously accepted or deleted reservation may return `404` if it has already been removed.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter verifies that returned `reservation.uid` matches the derived `user_id`.
 * > - If the reservation belongs to another user, the adapter returns `404` so the resource appears non-existent.
 * @summary Get reservation
 */
export const getReservationForAuthenticatedUser = async (
  reservationId: string,
  options?: RequestInit
): Promise<getReservationForAuthenticatedUserResponse> => {
  const res = await fetch(getGetReservationForAuthenticatedUserUrl(reservationId), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getReservationForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as getReservationForAuthenticatedUserResponse
}

export type deleteReservationForAuthenticatedUserResponse200 = {
  data: DeleteReservationApiResponse
  status: 200
}

export type deleteReservationForAuthenticatedUserResponse401 = {
  data: void
  status: 401
}

export type deleteReservationForAuthenticatedUserResponse403 = {
  data: void
  status: 403
}

export type deleteReservationForAuthenticatedUserResponse404 = {
  data: void
  status: 404
}

export type deleteReservationForAuthenticatedUserResponse422 = {
  data: void
  status: 422
}

export type deleteReservationForAuthenticatedUserResponse500 = {
  data: void
  status: 500
}

export type deleteReservationForAuthenticatedUserResponseSuccess =
  deleteReservationForAuthenticatedUserResponse200 & {
    headers: Headers
  }
export type deleteReservationForAuthenticatedUserResponseError = (
  | deleteReservationForAuthenticatedUserResponse401
  | deleteReservationForAuthenticatedUserResponse403
  | deleteReservationForAuthenticatedUserResponse404
  | deleteReservationForAuthenticatedUserResponse422
  | deleteReservationForAuthenticatedUserResponse500
) & {
  headers: Headers
}

export type deleteReservationForAuthenticatedUserResponse =
  | deleteReservationForAuthenticatedUserResponseSuccess
  | deleteReservationForAuthenticatedUserResponseError

export const getDeleteReservationForAuthenticatedUserUrl = (reservationId: string) => {
  return `/v1/reservations/${reservationId}`
}

/**
 * Deletes an existing reservation.
 * Use this when the user no longer wants to wait for the material.
 * The response indicates whether the underlying reservation record was successfully removed.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user access.
 * > - The adapter verifies reservation ownership via `reservation.uid` before deletion.
 * > - If the reservation belongs to another user, the adapter returns `404` so the resource appears non-existent.
 * @summary Delete reservation
 */
export const deleteReservationForAuthenticatedUser = async (
  reservationId: string,
  options?: RequestInit
): Promise<deleteReservationForAuthenticatedUserResponse> => {
  const res = await fetch(getDeleteReservationForAuthenticatedUserUrl(reservationId), {
    ...options,
    method: "DELETE",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: deleteReservationForAuthenticatedUserResponse["data"] = body ? JSON.parse(body) : {}
  return {
    data,
    status: res.status,
    headers: res.headers,
  } as deleteReservationForAuthenticatedUserResponse
}

export type getOrganizationResponse200 = {
  data: GetOrganizationApiResponse
  status: 200
}

export type getOrganizationResponse401 = {
  data: void
  status: 401
}

export type getOrganizationResponse403 = {
  data: void
  status: 403
}

export type getOrganizationResponse422 = {
  data: void
  status: 422
}

export type getOrganizationResponse500 = {
  data: void
  status: 500
}

export type getOrganizationResponseSuccess = getOrganizationResponse200 & {
  headers: Headers
}
export type getOrganizationResponseError = (
  | getOrganizationResponse401
  | getOrganizationResponse403
  | getOrganizationResponse422
  | getOrganizationResponse500
) & {
  headers: Headers
}

export type getOrganizationResponse = getOrganizationResponseSuccess | getOrganizationResponseError

export const getGetOrganizationUrl = (params?: GetOrganizationParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0
    ? `/v1/organizations?${stringifiedParams}`
    : `/v1/organizations`
}

/**
 * Returns organization details resolved by either the internal `organization_id` or an external `third_party_id`.
 * Use this endpoint to resolve an organization before making organization-scoped calls or when you need display, hierarchy, contact, or branding data for the organization.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user or server-to-server access.
 * > - For end users, omitted `third_party_id` defaults to the user's resolved municipality. An explicit value overrides this default.
 * > - Without a resolved end user, the client must send `organization_id` or `third_party_id`.
 * @summary Get organization
 */
export const getOrganization = async (
  params?: GetOrganizationParams,
  options?: RequestInit
): Promise<getOrganizationResponse> => {
  const res = await fetch(getGetOrganizationUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getOrganizationResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getOrganizationResponse
}

export type getOrganizationConfigsResponse200 = {
  data: GetOrganizationConfigsApiResponse
  status: 200
}

export type getOrganizationConfigsResponse401 = {
  data: void
  status: 401
}

export type getOrganizationConfigsResponse403 = {
  data: void
  status: 403
}

export type getOrganizationConfigsResponse404 = {
  data: void
  status: 404
}

export type getOrganizationConfigsResponse422 = {
  data: void
  status: 422
}

export type getOrganizationConfigsResponse500 = {
  data: void
  status: 500
}

export type getOrganizationConfigsResponseSuccess = getOrganizationConfigsResponse200 & {
  headers: Headers
}
export type getOrganizationConfigsResponseError = (
  | getOrganizationConfigsResponse401
  | getOrganizationConfigsResponse403
  | getOrganizationConfigsResponse404
  | getOrganizationConfigsResponse422
  | getOrganizationConfigsResponse500
) & {
  headers: Headers
}

export type getOrganizationConfigsResponse =
  getOrganizationConfigsResponseSuccess | getOrganizationConfigsResponseError

export const getGetOrganizationConfigsUrl = (params?: GetOrganizationConfigsParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0
    ? `/v1/organizations/configs?${stringifiedParams}`
    : `/v1/organizations/configs`
}

/**
 * Returns the organization configuration for an organization resolved by `organization_id` or `third_party_id`.
 * Use this endpoint when your integration needs organization-level settings that influence client behavior. Internal library system details and card credentials are intentionally excluded from the response DTO.
 *
 * > **Adapter specifics**
 * >
 * > - Requires a valid bearer token with end-user or server-to-server access.
 * > - For end users, omitted `third_party_id` defaults to the user's resolved municipality. An explicit value overrides this default.
 * > - Without a resolved end user, the client must send `organization_id` or `third_party_id`.
 * @summary Get organization configs
 */
export const getOrganizationConfigs = async (
  params?: GetOrganizationConfigsParams,
  options?: RequestInit
): Promise<getOrganizationConfigsResponse> => {
  const res = await fetch(getGetOrganizationConfigsUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getOrganizationConfigsResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getOrganizationConfigsResponse
}

export type getMetadataByMaterialIdsResponse200 = {
  data: GetMetadataApiResponse
  status: 200
}

export type getMetadataByMaterialIdsResponse400 = {
  data: void
  status: 400
}

export type getMetadataByMaterialIdsResponse401 = {
  data: void
  status: 401
}

export type getMetadataByMaterialIdsResponse403 = {
  data: void
  status: 403
}

export type getMetadataByMaterialIdsResponse500 = {
  data: void
  status: 500
}

export type getMetadataByMaterialIdsResponseSuccess = getMetadataByMaterialIdsResponse200 & {
  headers: Headers
}
export type getMetadataByMaterialIdsResponseError = (
  | getMetadataByMaterialIdsResponse400
  | getMetadataByMaterialIdsResponse401
  | getMetadataByMaterialIdsResponse403
  | getMetadataByMaterialIdsResponse500
) & {
  headers: Headers
}

export type getMetadataByMaterialIdsResponse =
  getMetadataByMaterialIdsResponseSuccess | getMetadataByMaterialIdsResponseError

export const getGetMetadataByMaterialIdsUrl = (params: GetMetadataByMaterialIdsParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["material_ids"]

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach(v => {
        normalizedParams.append(key, v === null ? "null" : String(v))
      })
      return
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0 ? `/v1/metadata?${stringifiedParams}` : `/v1/metadata`
}

/**
 * Returns metadata for up to 50 materials in a single request.
 * The input uses ISBN-13 values in `material_ids`. Materials that do not exist are silently omitted from the response instead of failing the entire request.
 * Use this endpoint together with inventory or reservation/loan data when you need catalog fields such as title, author, language, description, and publisher metadata.
 *
 * > **Adapter specifics**
 * >
 * > Requires a valid bearer token with end-user or server-to-server access.
 * @summary Get metadata for materials
 */
export const getMetadataByMaterialIds = async (
  params: GetMetadataByMaterialIdsParams,
  options?: RequestInit
): Promise<getMetadataByMaterialIdsResponse> => {
  const res = await fetch(getGetMetadataByMaterialIdsUrl(params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getMetadataByMaterialIdsResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getMetadataByMaterialIdsResponse
}

export type getMetadataByMaterialIdResponse200 = {
  data: GetMetadataApiResponse
  status: 200
}

export type getMetadataByMaterialIdResponse401 = {
  data: void
  status: 401
}

export type getMetadataByMaterialIdResponse403 = {
  data: void
  status: 403
}

export type getMetadataByMaterialIdResponse404 = {
  data: void
  status: 404
}

export type getMetadataByMaterialIdResponse500 = {
  data: void
  status: 500
}

export type getMetadataByMaterialIdResponseSuccess = getMetadataByMaterialIdResponse200 & {
  headers: Headers
}
export type getMetadataByMaterialIdResponseError = (
  | getMetadataByMaterialIdResponse401
  | getMetadataByMaterialIdResponse403
  | getMetadataByMaterialIdResponse404
  | getMetadataByMaterialIdResponse500
) & {
  headers: Headers
}

export type getMetadataByMaterialIdResponse =
  getMetadataByMaterialIdResponseSuccess | getMetadataByMaterialIdResponseError

export const getGetMetadataByMaterialIdUrl = (materialId: string) => {
  return `/v1/metadata/${materialId}`
}

/**
 * Returns metadata for a single material id.
 * Despite the singular path, the response shape is the same as the batch endpoint and still returns a `materials` array containing zero or one item.
 * Use this when you already know the ISBN-13 and want the same metadata structure as the bulk endpoint.
 *
 * > **Adapter specifics**
 * >
 * > Requires a valid bearer token with end-user or server-to-server access.
 * @summary Get metadata material
 */
export const getMetadataByMaterialId = async (
  materialId: string,
  options?: RequestInit
): Promise<getMetadataByMaterialIdResponse> => {
  const res = await fetch(getGetMetadataByMaterialIdUrl(materialId), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getMetadataByMaterialIdResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getMetadataByMaterialIdResponse
}

export type getSampleResponse200 = {
  data: GetSampleApiResponse
  status: 200
}

export type getSampleResponse401 = {
  data: void
  status: 401
}

export type getSampleResponse403 = {
  data: void
  status: 403
}

export type getSampleResponse404 = {
  data: void
  status: 404
}

export type getSampleResponse422 = {
  data: void
  status: 422
}

export type getSampleResponse429 = {
  data: void
  status: 429
}

export type getSampleResponse500 = {
  data: void
  status: 500
}

export type getSampleResponse502 = {
  data: void
  status: 502
}

export type getSampleResponseSuccess = getSampleResponse200 & {
  headers: Headers
}
export type getSampleResponseError = (
  | getSampleResponse401
  | getSampleResponse403
  | getSampleResponse404
  | getSampleResponse422
  | getSampleResponse429
  | getSampleResponse500
  | getSampleResponse502
) & {
  headers: Headers
}

export type getSampleResponse = getSampleResponseSuccess | getSampleResponseError

export const getGetSampleUrl = (materialId: string, params?: GetSampleParams) => {
  const normalizedParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value))
    }
  })

  const stringifiedParams = normalizedParams.toString()

  return stringifiedParams.length > 0
    ? `/v1/samples/${materialId}?${stringifiedParams}`
    : `/v1/samples/${materialId}`
}

/**
 * Returns a time-limited link to download the promotional sample of a material.
 * The sample is an excerpt of the material itself — an `epub` file for ebooks and an `mp3` file for audiobooks — intended for playback or download on a book page. The returned `sample_url` is a signed URL that is valid for 7 days and can be handed straight to a player or download link.
 * `format` is optional. When it is omitted the format is derived from the material type registered in Biblio API, so a client that only knows the ISBN-13 does not need to look up the material first. Pass it explicitly to skip that lookup.
 * The endpoint is being rolled out per environment, so it is not enabled everywhere yet; where it is not, Biblio API returns `403`.
 * Not every material has a sample; those return `404`. When `format` is omitted the material is looked up in order to derive it, so an unknown `material_id` returns `404` with a distinct `Material not found` message. When `format` is supplied there is no lookup, so an unknown material and a material without a sample both return `404` `Sample not available`.
 *
 * > **Adapter specifics**
 * >
 * > Requires a valid bearer token with end-user or server-to-server access.
 * @summary Get sample download link
 */
export const getSample = async (
  materialId: string,
  params?: GetSampleParams,
  options?: RequestInit
): Promise<getSampleResponse> => {
  const res = await fetch(getGetSampleUrl(materialId, params), {
    ...options,
    method: "GET",
  })

  const body = [204, 205, 304].includes(res.status) ? null : await res.text()

  const data: getSampleResponse["data"] = body ? JSON.parse(body) : {}
  return { data, status: res.status, headers: res.headers } as getSampleResponse
}
